import React, { useState, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useEmployee } from 'context/EmployeeContext';
import { getCompanyPayrolls, previewPayroll, getEmployeeProfile, getTimeOffBalances } from 'services/employeePortal';
import axiosInstance from '../../axios/axiosInstance';
import ENV from 'constants/env';

const companyId = () => localStorage.getItem('companyId') || ENV.COMPANY_ID;

async function downloadPaystubPdf(employeeId, payrollId, fileName) {
    try {
        const res = await axiosInstance.get(
            `/companies/${companyId()}/employees/${employeeId}/paystubs/${payrollId}/download`,
            { responseType: 'blob' }
        );
        const contentType = res.headers['content-type'] || 'text/html';
        const isPdf = contentType.includes('pdf');
        const blob = new Blob([res.data], { type: contentType });
        const url  = URL.createObjectURL(blob);

        if (isPdf) {
            // True PDF — direct download
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || `paystub-${payrollId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } else {
            // HTML pay stub — open in new tab; browser print dialog → Save as PDF
            const tab = window.open(url, '_blank');
            if (!tab) {
                // Popup blocked fallback — download with .pdf name so user knows to open/print
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName || `paystub-${payrollId}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        return true;
    } catch {
        return false;
    }
}

const fmt = (v) => `$${Number(v ?? 0).toFixed(2)}`;
const fmtDate = (val) => val
    ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })
    : '—';

const PRINT_ID = 'ep-paystub-print';

function printPayStub() {
    const el = document.getElementById(PRINT_ID);
    if (!el) return;
    const w = window.open('', '_blank', 'width=860,height=700');
    w.document.write(`<!DOCTYPE html><html><head><title>Pay Stub</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 24px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  td, th { padding: 5px 8px; }
  .text-end { text-align: right; }
  .fw-bold { font-weight: 700; }
  .section-header { font-size: 13px; font-weight: 700; border-bottom: 2px solid #E7730A; padding-bottom: 4px; margin-bottom: 6px; }
  .section-header-red { font-size: 13px; font-weight: 700; border-bottom: 2px solid #dc3545; padding-bottom: 4px; margin-bottom: 6px; }
  .stub-header { background: #fef9f5; border: 1px solid #f0d0b0; border-radius: 8px; padding: 12px 16px; display: flex; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 12px; }
  .lbl { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
  .net-row { background: #f0faf0; border: 1px solid #c3e6c3; border-radius: 8px; padding: 12px 16px; display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }
  .cols { display: flex; gap: 24px; }
  .col { flex: 1; }
  .orange { color: #fd7e14; }
  .green { color: #2e7d32; font-size: 20px; font-weight: 700; }
  .text-muted { color: #888; }
  hr { border: none; border-top: 1px solid #dee2e6; }
</style></head><body>`);
    w.document.write(el.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
}

function PayStubModal({ payroll, employeeId, employee, ytdData, isLatest, onClose }) {
    const [downloading, setDownloading] = useState(false);
    const [downloadFailed, setDownloadFailed] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        setDownloadFailed(false);
        const empName = [employee?.firstName, employee?.lastName].filter(Boolean).join('-') || 'paystub';
        const periodLabel = payroll.payDate ? payroll.payDate.slice(0, 10) : payroll.id;
        const ok = await downloadPaystubPdf(employeeId, payroll.id, `${empName}-${periodLabel}.pdf`);
        if (!ok) setDownloadFailed(true);
        setDownloading(false);
    };

    const { data: calc, isLoading } = useQuery({
        queryKey: ['ep-preview', payroll.id],
        queryFn: () => previewPayroll(payroll.id),
        staleTime: 1000 * 60 * 10,
    });

    const { data: timeOffBalances = [] } = useQuery({
        queryKey: ['ep-timeoff-balances', employeeId],
        queryFn: () => getTimeOffBalances(employeeId),
        enabled: !!employeeId,
        staleTime: 1000 * 60 * 5,
        retry: false,
    });

    const empData = useMemo(() => {
        if (!calc?.employees) return null;
        return calc.employees.find(e => e.employeeId === employeeId || e.id === employeeId);
    }, [calc, employeeId]);

    const period = `${fmtDate(payroll.startDate)} – ${fmtDate(payroll.endDate)}`;
    const address = employee
        ? [employee.addressLine1, employee.city, employee.state, employee.zip].filter(Boolean).join(', ')
        : '';

    const totalTaxes = empData
        ? (empData.federalIncomeTax ?? 0) + (empData.stateIncomeTax ?? 0) +
          (empData.socialSecurityTax ?? 0) + (empData.medicareTax ?? 0) +
          (empData.sdiTax ?? 0) + (empData.additionalMedicareTax ?? 0) + (empData.localIncomeTax ?? 0)
        : 0;
    const totalDeductions = empData
        ? (empData.preTaxDeductions ?? 0) + (empData.postTaxDeductions ?? 0)
        : 0;

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
            <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 780, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                {/* Header */}
                <div style={{ background: '#E7730A', color: '#fff', padding: '14px 20px', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>Pay Stub</span>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>

                <div style={{ padding: 20, fontSize: 13 }}>
                    {isLoading && <p style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>Loading pay stub...</p>}
                    {!isLoading && !empData && <p style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>No pay stub data found.</p>}

                    {empData && (
                        <div id={PRINT_ID}>
                            {/* Stub Header */}
                            <div style={{ background: '#fef9f5', border: '1px solid #f0d0b0', borderRadius: 8, padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 20 }}>
                                <div>
                                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Employee</div>
                                    <div style={{ fontWeight: 700 }}>{empData.employeeFirstName ?? empData.firstName ?? employee?.firstName} {empData.employeeLastName ?? empData.lastName ?? employee?.lastName}</div>
                                    {address && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{address}</div>}
                                </div>
                                <div>
                                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Period</div>
                                    <div style={{ fontWeight: 700 }}>{period}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Date</div>
                                    <div style={{ fontWeight: 700 }}>{fmtDate(payroll.payDate)}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Type</div>
                                    <div style={{ fontWeight: 700, textTransform: 'capitalize' }}>{empData.payType ?? payroll.type ?? '—'}</div>
                                </div>
                                {empData.paymentMethod && (
                                    <div>
                                        <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Payment Method</div>
                                        <div style={{ fontWeight: 700 }}>{(empData.paymentMethod || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                                    </div>
                                )}
                            </div>

                            {/* Earnings + Deductions */}
                            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                                {/* Earnings */}
                                <div style={{ flex: 1, minWidth: 280 }}>
                                    <div style={{ fontWeight: 700, borderBottom: '2px solid #E7730A', paddingBottom: 4, marginBottom: 10 }}>Earnings</div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ padding: '4px 0' }}>Gross Pay</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(empData.grossPay)}</td>
                                            </tr>
                                            {(empData.regularHours ?? empData.hours ?? 0) > 0 && (
                                                <tr style={{ color: '#555', fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>Regular Hours ({empData.regularHours ?? empData.hours} hrs)</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.regularPay ?? empData.regularWages)}</td>
                                                </tr>
                                            )}
                                            {(empData.overtimeHours ?? 0) > 0 && (
                                                <tr style={{ color: '#BF360C', fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>Overtime ({empData.overtimeHours} hrs @ 1.5x)</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.overtimePay ?? empData.overtimeWages)}</td>
                                                </tr>
                                            )}
                                            {(empData.ptoHours ?? 0) > 0 && (
                                                <tr style={{ color: '#1565C0', fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>PTO / Vacation ({empData.ptoHours} hrs)</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.ptoPay ?? empData.ptoWages)}</td>
                                                </tr>
                                            )}
                                            {(empData.sickHours ?? 0) > 0 && (
                                                <tr style={{ color: '#7B1FA2', fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>Sick Leave ({empData.sickHours} hrs)</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.sickPay ?? empData.sickWages)}</td>
                                                </tr>
                                            )}
                                            {(empData.paidHolidayHours ?? 0) > 0 && (
                                                <tr style={{ color: '#2E7D32', fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>Holiday ({empData.paidHolidayHours} hrs)</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.holidayPay ?? empData.paidHolidayWages)}</td>
                                                </tr>
                                            )}
                                            {(empData.bonusAmount ?? empData.bonus ?? 0) > 0 && (
                                                <tr style={{ fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>{payroll.type === 'correction' ? 'Correction Adjustment' : 'Bonus'}</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.bonusAmount ?? empData.bonus)}</td>
                                                </tr>
                                            )}
                                            {(empData.commissionAmount ?? empData.commission ?? 0) > 0 && (
                                                <tr style={{ fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>Commission</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.commissionAmount ?? empData.commission)}</td>
                                                </tr>
                                            )}
                                            {(empData.reimbursementAmount ?? empData.reimbursement ?? 0) > 0 && (
                                                <tr style={{ fontSize: 12 }}>
                                                    <td style={{ paddingLeft: 12 }}>Reimbursement</td>
                                                    <td style={{ textAlign: 'right' }}>{fmt(empData.reimbursementAmount ?? empData.reimbursement)}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {/* PTO / Time Off Balances — only on most recent paystub */}
                                    {isLatest && <div style={{ marginTop: 16, borderTop: '1px solid #dee2e6', paddingTop: 12 }}>
                                        <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                                            PTO / Time Off Balances <span style={{ fontSize: 10, color: '#aaa', textTransform: 'none' }}>(current)</span>
                                        </div>
                                        {timeOffBalances.length === 0 ? (
                                            <p style={{ fontSize: 12, color: '#aaa', margin: 0 }}>No time off policies assigned.</p>
                                        ) : (
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                                <thead>
                                                    <tr style={{ color: '#888' }}>
                                                        <th style={{ textAlign: 'left', padding: '2px 0', fontWeight: 500 }}>Policy</th>
                                                        <th style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 500 }}>Available</th>
                                                        <th style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 500 }}>Used YTD</th>
                                                        <th style={{ textAlign: 'right', padding: '2px 0', fontWeight: 500 }}>Pending</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {timeOffBalances.map(b => (
                                                        <tr key={b.id}>
                                                            <td style={{ padding: '3px 0', color: '#555' }}>{b.policy?.name ?? b.policy?.timeOffType ?? 'PTO'}</td>
                                                            <td style={{ textAlign: 'right', padding: '3px 4px', color: '#1565C0', fontWeight: 600 }}>{b.balanceHours ?? 0} hrs</td>
                                                            <td style={{ textAlign: 'right', padding: '3px 4px', color: '#dc3545' }}>{b.usedHours ?? 0} hrs</td>
                                                            <td style={{ textAlign: 'right', padding: '3px 0', color: '#fd7e14' }}>{b.pendingHours ?? 0} hrs</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>}
                                </div>

                                {/* Deductions & Taxes */}
                                <div style={{ flex: 1, minWidth: 280 }}>
                                    <div style={{ fontWeight: 700, borderBottom: '2px solid #dc3545', paddingBottom: 4, marginBottom: 10 }}>Deductions &amp; Taxes</div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <tbody>
                                            {/* Pre-Tax Benefits from snapshot */}
                                            {Array.isArray(empData.benefitItems) && empData.benefitItems.filter(b => b.isPreTax).length > 0 && (
                                                <tr><td colSpan={2} style={{ padding: '4px 0 2px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pre-Tax Benefits</td></tr>
                                            )}
                                            {Array.isArray(empData.benefitItems) && empData.benefitItems.filter(b => b.isPreTax).map((b, i) => (
                                                <React.Fragment key={i}>
                                                    <tr style={{ borderBottom: (b.employerAmount ?? 0) > 0 ? 'none' : '1px solid #f0f0f0' }}>
                                                        <td style={{ padding: '3px 0 3px 8px', fontSize: 12 }}>{b.name}</td>
                                                        <td style={{ textAlign: 'right', color: '#dc3545', fontSize: 12 }}>({fmt(b.employeeAmount)})</td>
                                                    </tr>
                                                    {(b.employerAmount ?? 0) > 0 && (
                                                        <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                            <td style={{ padding: '2px 0 2px 16px', fontSize: 10, color: '#2e7d32', fontStyle: 'italic' }}>↳ Employer contribution</td>
                                                            <td style={{ textAlign: 'right', fontSize: 10, color: '#2e7d32' }}>{fmt(b.employerAmount)}</td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            {/* Fallback: no snapshot items but has pre-tax total */}
                                            {!Array.isArray(empData.benefitItems) && (empData.preTaxDeductions ?? 0) > 0 && (
                                                <>
                                                    <tr><td colSpan={2} style={{ padding: '4px 0 2px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pre-Tax Benefits</td></tr>
                                                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                        <td style={{ padding: '3px 0 3px 8px', fontSize: 12 }}>Pre-Tax Deductions</td>
                                                        <td style={{ textAlign: 'right', color: '#dc3545', fontSize: 12 }}>({fmt(empData.preTaxDeductions)})</td>
                                                    </tr>
                                                </>
                                            )}
                                            {/* Taxes */}
                                            <tr><td colSpan={2} style={{ padding: '6px 0 2px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Taxes</td></tr>
                                            {(empData.federalIncomeTax ?? 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '4px 0 4px 8px' }}>Federal Income Tax (FITW)</td>
                                                    <td style={{ textAlign: 'right', color: '#fd7e14' }}>{fmt(empData.federalIncomeTax)}</td>
                                                </tr>
                                            )}
                                            {(empData.stateIncomeTax ?? 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '4px 0 4px 8px' }}>State Income Tax (SIT)</td>
                                                    <td style={{ textAlign: 'right', color: '#fd7e14' }}>{fmt(empData.stateIncomeTax)}</td>
                                                </tr>
                                            )}
                                            {(empData.socialSecurityTax ?? 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '4px 0 4px 8px' }}>Social Security Tax</td>
                                                    <td style={{ textAlign: 'right', color: '#fd7e14' }}>{fmt(empData.socialSecurityTax)}</td>
                                                </tr>
                                            )}
                                            {(empData.medicareTax ?? 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '4px 0 4px 8px' }}>Medicare Tax (1.45%)</td>
                                                    <td style={{ textAlign: 'right', color: '#fd7e14' }}>{fmt(empData.medicareTax)}</td>
                                                </tr>
                                            )}
                                            {(empData.sdiTax ?? 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '4px 0 4px 8px' }}>State Disability (SDI)</td>
                                                    <td style={{ textAlign: 'right', color: '#fd7e14' }}>{fmt(empData.sdiTax)}</td>
                                                </tr>
                                            )}
                                            {(empData.additionalMedicareTax ?? 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '4px 0 4px 8px' }}>Additional Medicare (0.9%)</td>
                                                    <td style={{ textAlign: 'right', color: '#fd7e14' }}>{fmt(empData.additionalMedicareTax)}</td>
                                                </tr>
                                            )}
                                            {(empData.localIncomeTax ?? 0) > 0 && (
                                                <tr>
                                                    <td style={{ padding: '4px 0 4px 8px' }}>Local / City Tax</td>
                                                    <td style={{ textAlign: 'right', color: '#fd7e14' }}>{fmt(empData.localIncomeTax)}</td>
                                                </tr>
                                            )}
                                            {/* Post-Tax Deductions from snapshot */}
                                            {Array.isArray(empData.benefitItems) && empData.benefitItems.filter(b => !b.isPreTax).length > 0 && (
                                                <tr><td colSpan={2} style={{ padding: '6px 0 2px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Post-Tax Deductions</td></tr>
                                            )}
                                            {Array.isArray(empData.benefitItems) && empData.benefitItems.filter(b => !b.isPreTax).map((b, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '3px 0 3px 8px', fontSize: 12 }}>{b.name}</td>
                                                    <td style={{ textAlign: 'right', color: '#6c757d', fontSize: 12 }}>({fmt(b.employeeAmount)})</td>
                                                </tr>
                                            ))}
                                            {/* Fallback post-tax total */}
                                            {!Array.isArray(empData.benefitItems) && (empData.postTaxDeductions ?? 0) > 0 && (
                                                <>
                                                    <tr><td colSpan={2} style={{ padding: '6px 0 2px', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Post-Tax Deductions</td></tr>
                                                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                        <td style={{ padding: '3px 0 3px 8px', fontSize: 12 }}>Post-Tax Deductions</td>
                                                        <td style={{ textAlign: 'right', color: '#6c757d', fontSize: 12 }}>({fmt(empData.postTaxDeductions)})</td>
                                                    </tr>
                                                </>
                                            )}
                                            <tr style={{ borderTop: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '6px 0', fontWeight: 700 }}>Total Taxes</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#fd7e14' }}>{fmt(totalTaxes)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '4px 0', fontWeight: 700 }}>Total Deductions</td>
                                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc3545' }}>{fmt(totalDeductions)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* YTD Summary */}
                            {ytdData && (
                                <div style={{ background: '#f7f9fc', border: '1px solid #d0d8e4', borderRadius: 8, padding: '10px 16px', marginTop: 16 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Year-to-Date Summary</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', fontSize: 12 }}>
                                        {[
                                            { label: 'YTD Gross',   value: ytdData.ytdGross,    color: '#222' },
                                            { label: 'Fed Tax',     value: ytdData.ytdFedTax,   color: '#fd7e14' },
                                            { label: 'SS Tax',      value: ytdData.ytdSSTax,    color: '#fd7e14' },
                                            { label: 'Medicare',    value: ytdData.ytdMedTax,   color: '#fd7e14' },
                                            { label: 'State Tax',   value: ytdData.ytdStateTax, color: '#fd7e14' },
                                            { label: 'Deductions',  value: ytdData.ytdPreDeds + ytdData.ytdPostDeds, color: '#dc3545' },
                                            { label: 'YTD Net Pay', value: ytdData.ytdNetPay,   color: '#2e7d32' },
                                        ].map(({ label, value, color }) => (
                                            <div key={label}>
                                                <div style={{ color: '#888', marginBottom: 1 }}>{label}</div>
                                                <div style={{ fontWeight: 700, color }}>{fmt(value)}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Net Pay Summary */}
                            <div style={{ background: '#f0faf0', border: '1px solid #c3e6c3', borderRadius: 8, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                                <div style={{ fontSize: 12, color: '#555' }}>
                                    Gross Pay {fmt(empData.grossPay)}
                                    &nbsp;−&nbsp; Deductions {fmt(totalDeductions)}
                                    &nbsp;−&nbsp; Taxes {fmt(totalTaxes)}
                                </div>
                                <div>
                                    <span style={{ fontSize: 13, color: '#555', marginRight: 8 }}>Net Pay</span>
                                    <span style={{ fontSize: 22, fontWeight: 700, color: '#2e7d32' }}>{fmt(empData.netPay)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                        {downloadFailed && (
                            <span style={{ fontSize: 12, color: '#dc3545' }}>
                                PDF download unavailable — use Print instead.
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={onClose} style={{ background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 13 }}>Close</button>
                        {empData && (
                            <>
                                <button
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    style={{ background: '#1a9fbb', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: downloading ? 'default' : 'pointer', fontSize: 13, fontWeight: 600, opacity: downloading ? 0.7 : 1 }}
                                >
                                    {downloading ? 'Downloading...' : 'Download PDF'}
                                </button>
                                <button onClick={printPayStub} style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    Print
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const Paystubs = () => {
    const { employeeId } = useEmployee();
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [selectedPayroll, setSelectedPayroll] = useState(null);

    const { data: employee } = useQuery({
        queryKey: ['ep-employee', employeeId],
        queryFn: () => getEmployeeProfile(employeeId),
        enabled: !!employeeId,
    });

    const { data: allPayrolls = [], isLoading: loadingPayrolls } = useQuery({
        queryKey: ['ep-company-payrolls'],
        queryFn: getCompanyPayrolls,
    });

    const approvedPayrolls = useMemo(
        () => allPayrolls.filter(p => p.status === 'approved'),
        [allPayrolls]
    );

    const previewQueries = useQueries({
        queries: approvedPayrolls.map(p => ({
            queryKey: ['ep-preview', p.id],
            queryFn: () => previewPayroll(p.id),
            enabled: !!employeeId,
            staleTime: 1000 * 60 * 10,
        })),
    });

    const previewsLoading = previewQueries.some(q => q.isLoading);

    const employeePayrolls = useMemo(() => {
        if (!employeeId || previewsLoading) return [];
        return approvedPayrolls.filter((_, i) => {
            const preview = previewQueries[i]?.data;
            if (!preview?.employees) return false;
            return preview.employees.some(e =>
                (e.employeeId === employeeId || e.id === employeeId) &&
                (e.grossPay ?? 0) > 0
            );
        });
    }, [approvedPayrolls, previewQueries, previewsLoading, employeeId]);

    // Calculate YTD totals from all approved payrolls in the same calendar year
    const ytdData = useMemo(() => {
        if (!employeeId || previewsLoading || !selectedPayroll) return null;
        const yr = selectedPayroll.payDate ? new Date(selectedPayroll.payDate).getUTCFullYear() : new Date().getFullYear();
        let ytdGross = 0, ytdFedTax = 0, ytdStateTax = 0, ytdSSTax = 0, ytdMedTax = 0;
        let ytdPreDeds = 0, ytdPostDeds = 0, ytdNetPay = 0;
        approvedPayrolls.forEach((p, i) => {
            const pYr = p.payDate ? new Date(p.payDate).getUTCFullYear() : null;
            if (pYr !== yr) return;
            const emp = previewQueries[i]?.data?.employees?.find(e => e.employeeId === employeeId || e.id === employeeId);
            if (!emp) return;
            ytdGross    += emp.grossPay ?? 0;
            ytdFedTax   += emp.federalIncomeTax ?? 0;
            ytdStateTax += emp.stateIncomeTax ?? 0;
            ytdSSTax    += emp.socialSecurityTax ?? 0;
            ytdMedTax   += emp.medicareTax ?? 0;
            ytdPreDeds  += emp.preTaxDeductions ?? 0;
            ytdPostDeds += emp.postTaxDeductions ?? 0;
            ytdNetPay   += emp.netPay ?? 0;
        });
        return ytdGross > 0 ? { ytdGross, ytdFedTax, ytdStateTax, ytdSSTax, ytdMedTax, ytdPreDeds, ytdPostDeds, ytdNetPay } : null;
    }, [approvedPayrolls, previewQueries, previewsLoading, employeeId, selectedPayroll]);

    const isLoading = loadingPayrolls || previewsLoading;
    const totalPages = Math.ceil(employeePayrolls.length / rowsPerPage);
    const paginated = employeePayrolls.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    return (
        <div style={{ padding: '24px', backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <h4 style={{ color: '#1a9fbb', fontWeight: 700, marginBottom: 4 }}>Paystubs</h4>
            <p style={{ color: '#6c757d', marginBottom: 16, fontSize: 14 }}>
                Easily view, download, and manage your pay stubs, including detailed earnings, deductions, and tax information – all in one place.
            </p>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                    <thead>
                        <tr style={{ background: '#f0f9fb' }}>
                            {['Payday', 'Period', 'Net Pay', 'Gross Pay', 'Total Taxes', 'Deductions', 'Action'].map(h => (
                                <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: '#1a9fbb', fontWeight: 600, fontSize: 13 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>⏳ Loading paystubs...</td></tr>
                        ) : paginated.length === 0 ? (
                            <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No paystubs found for your account.</td></tr>
                        ) : paginated.map((p) => {
                            const preview = previewQueries[approvedPayrolls.indexOf(p)]?.data;
                            const empData = preview?.employees?.find(e => e.employeeId === employeeId || e.id === employeeId);
                            const netPay = empData?.netPay ?? p.netPay ?? 0;
                            const grossPay = empData?.grossPay ?? p.grossPay ?? 0;
                            const taxes = (empData?.federalIncomeTax ?? 0) + (empData?.stateIncomeTax ?? 0) + (empData?.socialSecurityTax ?? 0) + (empData?.medicareTax ?? 0) + (empData?.sdiTax ?? 0) + (empData?.additionalMedicareTax ?? 0) + (empData?.localIncomeTax ?? 0);
                            const deductions = (empData?.preTaxDeductions ?? 0) + (empData?.postTaxDeductions ?? 0);
                            return (
                                <tr key={p.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.payDate)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmtDate(p.startDate)} – {fmtDate(p.endDate)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(netPay)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(grossPay)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(taxes)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>{fmt(deductions)}</td>
                                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                                        <button
                                            onClick={() => setSelectedPayroll(p)}
                                            style={{ background: '#E7730A', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}
                                        >
                                            Pay Stub
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>

                <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, fontSize: 13, color: '#6c757d' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ whiteSpace: 'nowrap' }}>Rows per page:</span>
                        <select
                            value={rowsPerPage}
                            onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                            style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 28px 4px 10px', fontSize: 13, color: '#333', background: '#fff', cursor: 'pointer', appearance: 'auto', minWidth: 64 }}
                        >
                            {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                    <span style={{ whiteSpace: 'nowrap' }}>
                        {employeePayrolls.length === 0
                            ? '0 of 0'
                            : `${(page - 1) * rowsPerPage + 1}–${Math.min(page * rowsPerPage, employeePayrolls.length)} of ${employeePayrolls.length}`}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {[
                            { label: '«', action: () => setPage(1), disabled: page === 1, title: 'First page' },
                            { label: '‹', action: () => setPage(p => Math.max(1, p - 1)), disabled: page === 1, title: 'Previous page' },
                            { label: '›', action: () => setPage(p => Math.min(totalPages, p + 1)), disabled: page === totalPages || totalPages === 0, title: 'Next page' },
                            { label: '»', action: () => setPage(totalPages), disabled: page === totalPages || totalPages === 0, title: 'Last page' },
                        ].map(({ label, action, disabled, title }) => (
                            <button key={label} onClick={action} disabled={disabled} title={title}
                                style={{
                                    width: 32, height: 32, borderRadius: 6, border: '1px solid #dee2e6',
                                    background: disabled ? '#f8f9fa' : '#fff',
                                    color: disabled ? '#bbb' : '#495057',
                                    cursor: disabled ? 'default' : 'pointer',
                                    fontSize: 14, fontWeight: 600,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background 0.15s',
                                }}
                            >{label}</button>
                        ))}
                    </div>
                </div>
            </div>

            {selectedPayroll && (
                <PayStubModal
                    payroll={selectedPayroll}
                    employeeId={employeeId}
                    employee={employee}
                    ytdData={ytdData}
                    isLatest={employeePayrolls.length > 0 && selectedPayroll.id === employeePayrolls[0].id}
                    onClose={() => setSelectedPayroll(null)}
                />
            )}
        </div>
    );
};

export default Paystubs;
